import { trackEvent } from "@kunduz/utility-analytics";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { ReactElement, useEffect } from "react";
import styles from "./homePage.module.scss";
import { QuestionIndexing } from "@kunduz/data-access-qi-api";
import { QIBreadcrumb } from "../../components/breadcrumb";
import Sticky from "../../../../common/components/sticky";
import { QuestionItem } from "@kunduz/feature-question-indexing";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import Pagination from "../../components/pagination/pagination";
import Link from "next/link";
import { getQIUrl } from "@kunduz/utility-common";
import { QIFilterMenu } from "../../components/qiFilterMenu/qiFilterMenu";
import {
  HeaderComponent,
  WebsiteEnFooter,
} from "@kunduz/feature-landing-pages";
import { type IContent } from "@kunduz/data-access-cms-api";

const HomeSUGPage = (props: Props): ReactElement => {
  const { t } = useTranslation("questionIndexing");

  useEffect(() => {
    trackEvent("Question Indexing Homepage Visited");
  }, []);

  const alternates = [];
  alternates.push({
    hrefLang: "tr",
    href: getQIUrl({
      lang: "tr",
      domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
    }),
  });

  return (
    <>
      <NextSeo
        title={t("course_list_meta_title")}
        description={t("course_list_meta_description")}
        canonical={getQIUrl({
          lang: props.lang,
          domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
        })}
        languageAlternates={alternates}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />

      <HeaderComponent
        language={props.lang}
        headerContactShow={false}
        headerBannerShow={!!props.headerBanner}
        headerBannerData={props.headerBanner}
        headerShow={!!props.headerItems}
        headerItems={props.headerItems}
      />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <QIBreadcrumb lang={props.lang} linkAs={Link} />
        </div>
        <aside className={styles.filterMenuContainer}>
          <QIFilterMenu
            courses={props.courses}
            coursesTitle={t("question_indexing_course_list_page_title")}
            lang={props.lang}
            linkAs={Link}
          />
        </aside>
        <main className={styles.content}>
          <div className={styles.courseContainer}>
            <div className={styles.headersContainer}>
              <h1>{t("question_indexing_course_list_page_title")}</h1>
              {!props.isMobile && (
                <div className={styles.shortDesc}>
                  {t("question_indexing_course_list_page_introduction_text")}
                </div>
              )}

              {props.questions && (
                <>
                  <section id="questions" className={styles.questions}>
                    {props.questions.map((question, index) => (
                      <QuestionItem
                        key={index}
                        description={question.text_content}
                        imageUrl={question.image}
                        imgAlt={question.text_content}
                        subjectName={question.subject}
                        courseName={question.course}
                        url={getQIUrl({
                          lang: props.lang,
                          questionSlug: question.slug,
                          questionId: question.id,
                        })}
                        linkAs={Link}
                      />
                    ))}
                  </section>

                  <Pagination
                    base={getQIUrl({
                      lang: props.lang,
                    })}
                    currentPage={props.page}
                    perPage={props.perPage}
                    total={props.total}
                    linkAs={Link}
                  />
                </>
              )}
            </div>
          </div>
        </main>
        <aside className={styles.sticky}>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={props.isMobile}
              lang={props.lang}
              mode={SideCTAModes.QI}
              translationFile="questionIndexing"
            />
          </Sticky>
        </aside>
      </div>

      <WebsiteEnFooter />
    </>
  );
};

interface Props {
  courses: QuestionIndexing.IQiCourse[];
  questions: QuestionIndexing.UsQiQuestion[];
  total: number;
  perPage: number;
  lang: "tr" | "en";
  isMobile: boolean;
  page: number;
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}

export default HomeSUGPage;
